import React from 'react';
import { parseMarkdownLite, type InlineNode } from '../utils/markdownLite';

// Renders a narrative field written in Markdown-lite (see utils/markdownLite.ts).
// `lead` is placed at the start of the first paragraph — used for the AI provenance mark.
const renderInline = (nodes: InlineNode[], keyPrefix = ''): React.ReactNode[] =>
  nodes.map((n, i) => {
    const key = `${keyPrefix}${i}`;
    switch (n.type) {
      case 'strong': return <strong key={key} className="font-semibold">{renderInline(n.children, `${key}.`)}</strong>;
      case 'em': return <em key={key}>{renderInline(n.children, `${key}.`)}</em>;
      case 'link':
        return (
          <a key={key} href={n.href} target="_blank" rel="noopener noreferrer"
            className="underline decoration-gray-400 underline-offset-2 hover:text-emerald-700 dark:hover:text-emerald-400 break-words">
            {renderInline(n.children, `${key}.`)}
          </a>
        );
      default: return <React.Fragment key={key}>{n.text}</React.Fragment>;
    }
  });

const MarkdownText: React.FC<{ text?: string | null; className?: string; lead?: React.ReactNode }> = ({
  text, className = 'text-sm text-gray-700 dark:text-gray-300', lead,
}) => {
  const blocks = parseMarkdownLite(text);
  if (!blocks.length) return lead ? <div className={className}>{lead}</div> : null;
  const leadNode = lead ? <>{lead} </> : null;
  return (
    <div className={`${className} space-y-2`}>
      {blocks.map((b, i) => {
        if (b.type === 'paragraph') {
          return <p key={i} className="whitespace-pre-wrap break-words">{i === 0 ? leadNode : null}{renderInline(b.children)}</p>;
        }
        const List = b.ordered ? 'ol' : 'ul';
        return (
          <React.Fragment key={i}>
            {i === 0 && leadNode ? <p>{leadNode}</p> : null}
            <List className={`${b.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-0.5`}>
              {b.items.map((item, j) => <li key={j} className="whitespace-pre-wrap break-words">{renderInline(item, `${j}.`)}</li>)}
            </List>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MarkdownText;
